import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import mlcuLogo from "../assets/mlcu-logo.png";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            const u = await login(identifier.trim(), password);
            toast.success(`Welcome, ${u.name}`);
            navigate(["admin", "superadmin"].includes(u.role) ? "/admin" : "/operator", { replace: true });
        } catch (err) {
            setError(formatApiError(err.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-white">
            {/* Left — Brand panel */}
            <div
                className="hidden lg:flex flex-col justify-between p-12 relative bg-cover bg-center"
                style={{
                    backgroundImage:
                        "linear-gradient(rgba(255,255,255,0.92), rgba(255,255,255,0.92)), url(https://images.unsplash.com/photo-1666166834752-6b5f65d11b8b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)",
                }}
            >
                <Logo size="md" />
                <div>
                    <p className="label-caps mb-4">Bus Attendance · QR Verification</p>
                    <h1 className="font-display text-6xl leading-[0.95] mb-6 text-[#111111]">
                        Martin Luther<br />
                        <span className="text-[#FF3B30]">Christian University</span>
                    </h1>
                    <p className="text-[#666666] text-base max-w-md">
                        MLCU Bus Attendance & Payment Verification System verifies student payment status in real time while logging
                        every scan for audit — no specialized hardware required.
                    </p>
                </div>
                {/* <div className="flex gap-8 label-caps">
                    <div>
                        <div className="font-display text-3xl text-[#111111] mb-0">18</div>
                        <span>Students</span>
                    </div>
                    <div>
                        <div className="font-display text-3xl text-[#111111] mb-0">03</div>
                        <span>Buses</span>
                    </div>
                    <div>
                        <div className="font-display text-3xl text-[#FF3B30] mb-0">Live</div>
                        <span>Demo</span>
                    </div>
                </div> */}
            </div>

            {/* Right — Form */}
            <div className="flex items-center justify-center p-6 sm:p-12">
                <div className="w-full max-w-md">
                    <div className="lg:hidden mb-10">
                        <Logo size="md" />
                    </div>
                    <img
                        src={mlcuLogo}
                        alt="Martin Luther Christian University"
                        data-testid="mlcu-logo"
                        className="h-20 w-auto object-contain mb-6 select-none pointer-events-none"
                        draggable={false}
                    />
                    <p className="label-caps mb-3">Secure Sign In</p>
                    <h2 className="font-display text-4xl text-[#111111] mb-2">Welcome back</h2>
                    <p className="text-[#666666] mb-10">
                        Sign in as admin or operator to continue.
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
                        <div>
                            <label className="label-caps block mb-2" htmlFor="identifier">
                                Email or Operator ID
                            </label>
                            <input
                                id="identifier"
                                data-testid="login-email-input"
                                type="text"
                                autoCapitalize="none"
                                required
                                value={identifier}
                                onChange={(e) => setIdentifier(e.target.value)}
                                className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-3 focus:border-[#111111] focus:ring-1 focus:ring-[#111111] outline-none transition-all"
                                placeholder="Email or Operator ID"
                            />
                        </div>
                        <div>
                            <label className="label-caps block mb-2" htmlFor="password">
                                Password
                            </label>
                            <input
                                id="password"
                                data-testid="login-password-input"
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-[#F8F9FA] border border-[#E5E5E5] px-4 py-3 focus:border-[#111111] focus:ring-1 focus:ring-[#111111] outline-none transition-all"
                                placeholder="••••••••"
                            />
                        </div>
                        {error && (
                            <div
                                data-testid="login-error"
                                className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 text-sm font-medium"
                            >
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-button"
                            className="w-full bg-[#FF3B30] text-white font-bold px-6 py-3 hover:bg-[#D70015] focus:ring-2 focus:ring-[#FF3B30] focus:ring-offset-2 transition-colors disabled:opacity-60"
                        >
                            {loading ? "Signing in…" : "Sign In"}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}
